<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
import { computed, type WritableComputedRef } from "vue";
import { Building } from "@lucide/vue";

import { WizardProjectType, services as BuildingProjectServices, useEditBuildingProjectWizard } from "@/features/BuildingProject";

import { useBuildingTypes } from "@/dataProviders/BuildingTypes";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";
import type { HeatedAreaGroup } from "@/features/BuildingProject/services/ValidateBuildingProjectService";
import { buildingTypeDisplayLabel } from "@/features/BuildingProject/utils/buildingTypeLabel";
import { weightsMixedUse } from "@/features/BuildingProject/utils/calculationLimitValue";
import {
  normalizeRow,
  projectTotalsFromRows,
  rowsFromProject,
  type BuildingAreaRow,
} from "@/features/BuildingProject/utils/buildingAreaTable";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import StepIntro from "../StepIntro/StepIntro.vue";
import BuildingAreaTable from "./BuildingAreaTable.vue";

const props = withDefaults(
  defineProps<{
    wasValidated: boolean;
    project: WizardProjectType;
    /** Suppresses this component's own StepIntro, for BuildingWithReferenceArea, which renders one heading above both halves. */
    hideIntro?: boolean;
  }>(),
  { hideIntro: false }
);

const { isExistingProject, field, lens } = useWizardProject(() => props.project);

const buildingTypes = useBuildingTypes();

const buildingTypeEnum = field("buildingTypeEnum");

const buildingUses = lens(
  (current) => current.buildingUses ?? [],
  (current, value) => (current.buildingUses = value)
);

const areaAboveGround = lens(
  (current) => current.emissionCalculationAssumptions.referencedAreas.areaAboveGround,
  (current, value) => (current.emissionCalculationAssumptions.referencedAreas.areaAboveGround = value)
);
const floorsAboveGround = lens(
  (current) => current.emissionCalculationAssumptions.referencedAreas.floorsAboveGround,
  (current, value) => (current.emissionCalculationAssumptions.referencedAreas.floorsAboveGround = value)
);
const heatedFloorAreaAboveGround = lens(
  (current) => current.emissionCalculationAssumptions.heatArea.heatedFloorAreaAboveGround,
  (current, value) => (current.emissionCalculationAssumptions.heatArea.heatedFloorAreaAboveGround = value)
);
const areaBelowGround = lens(
  (current) => current.emissionCalculationAssumptions.referencedAreas.areaBelowGround,
  (current, value) => (current.emissionCalculationAssumptions.referencedAreas.areaBelowGround = value)
);
const basementFloors = lens(
  (current) => current.emissionCalculationAssumptions.referencedAreas.basementFloors,
  (current, value) => (current.emissionCalculationAssumptions.referencedAreas.basementFloors = value)
);
const heatedBasementArea = lens(
  (current) => current.emissionCalculationAssumptions.heatArea.heatedBasementArea,
  (current, value) => (current.emissionCalculationAssumptions.heatArea.heatedBasementArea = value)
);

const calculationTypes = useCalculationTypes();
const selectedCalculationType = computed(() => {
  const id = props.project.emissionCalculationAssumptions.calculationType.calculationTypeEnum;
  return id === null || id === undefined ? undefined : calculationTypes.getSelectedCalculationType(id);
});

/** Mixed use exists only under BR18 (2025)'s calculation types, where the limit is area-weighted. */
const canAddRows = computed(() => weightsMixedUse(selectedCalculationType.value));

const weightableBuildingTypeIds = computed(() => [
  ...new Set(
    (selectedCalculationType.value?.thresholdByBuildingType ?? [])
      .filter((row) => row.threshold !== null && row.threshold !== undefined)
      .map((row) => Number(row.buildingTypeId))
  ),
]);

const buildingTypeOptions = computed(() =>
  buildingTypes.getBuildingTypes().map((buildingType) => ({
    name: buildingTypeDisplayLabel(buildingType.label),
    value: Number(buildingType.id),
    disabled: buildingType.disabled,
  }))
);

const rows = computed(() => rowsFromProject(props.project));

// Written only on change, so an untouched field keeps the exact value (and type) it was loaded with.
const assign = <T,>(target: WritableComputedRef<T>, value: T) => {
  if (target.value !== value) target.value = value;
};

/** One row is the project itself; two or more are stored as uses, with their totals on the project. */
const writeRows = (next: BuildingAreaRow[]) => {
  const single = next.length <= 1;
  const totals = single ? projectTotalsFromRows(next.slice(0, 1)) : projectTotalsFromRows(next);
  if (totals.buildingTypeId !== null || buildingTypeEnum.value !== undefined) assign(buildingTypeEnum, totals.buildingTypeId);
  assign(areaAboveGround, totals.areaAboveGround);
  assign(floorsAboveGround, totals.floorsAboveGround);
  assign(heatedFloorAreaAboveGround, totals.heatedAreaAboveGround);
  assign(areaBelowGround, totals.areaBelowGround);
  assign(basementFloors, totals.floorsBelowGround);
  assign(heatedBasementArea, totals.heatedAreaBelowGround);
  if (single) {
    if (buildingUses.value.length > 0) buildingUses.value = [];
  } else {
    buildingUses.value = next.map(normalizeRow);
  }
};

const editBuildingProjectWizard = useEditBuildingProjectWizard();

/** On an existing single-use project only a pair edited since the last save is held to its gross area, as the Save gate does. */
const heatedAreaChecked = (_rowIndex: number, side: HeatedAreaGroup) =>
  !isExistingProject.value ||
  rows.value.length >= 2 ||
  BuildingProjectServices.validateBuildingProject.isHeatedAreaGroupEdited(
    side,
    props.project,
    editBuildingProjectWizard.getSavedProject()
  );

const countryComment = computed(() => {
  switch (props.project.buildingAddress.country) {
    case "Norway":
      return "Emissions are calculated using the GFA (Gross Floor Area), which is the total area above and below ground.";
    case "Denmark":
      return "Heated area is used for calculating the emissions from the B6-module.";
    default:
      return null;
  }
});
</script>

<template>
  <div class="w-full">
    <StepIntro
      v-if="!hideIntro"
      class="mb-6"
      heading="How big is the building?"
      :hint="countryComment || 'Gross area above and below ground, and how much of that is heated.'" />

    <div class="rounded-8 bg-slate-0 p-px-20 @container flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Building
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <CardHeading class="t-sh4 text-type-default">Building area</CardHeading>
        </div>
        <p
          v-if="canAddRows"
          class="t-caption text-type-muted">
          Add a row per building type. The type with the largest gross area becomes the main type. Each type's limit value is
          weighted by its share of the total area. Floors and heated area unlock once the ground area is set.
        </p>
        <p
          v-else
          class="t-caption text-type-muted">
          Building type decides which limit value applies. Floors and heated area unlock once each ground area is set. No
          basement? Leave below ground empty or at 0.
        </p>
      </div>

      <BuildingAreaTable
        :rows="rows"
        :building-type-options="buildingTypeOptions"
        :weightable-building-type-ids="weightableBuildingTypeIds"
        :can-add-rows="canAddRows"
        :was-validated="wasValidated"
        :is-existing-project="isExistingProject"
        :heated-area-checked="heatedAreaChecked"
        @update:rows="writeRows" />
    </div>
  </div>
</template>
